import type { LabelPrintResult, LabelType } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, DatePicker, Form, Input, InputNumber, Segmented, Select, Space, Typography, message } from 'antd';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { hq } from '../api/client';

interface LabelForm {
  materialId: string;
  type: LabelType;
  code?: string;
  batchNo?: string;
  expiryDate?: Dayjs;
  copies: number;
}

/**
 * 标签打印（PDA/仓库共用）：条码 / RFID EPC 标签，服务端返回 ZPL（热敏机直发）与 HTML（浏览器打印）。
 * 腕带场景选择「条码」版式，在备注/编码位置填写腕带编号即可复用同一模板。
 */
export default function LabelPrintPage() {
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm<LabelForm>();
  const [preview, setPreview] = useState<LabelPrintResult | null>(null);
  const type = Form.useWatch('type', form) ?? 'BARCODE';

  const materials = useQuery({
    queryKey: ['material', 'label-options', keyword],
    queryFn: () => hq.material.list({ keyword, size: 20 }),
    enabled: keyword.trim().length > 0
  });

  const render = useMutation({
    mutationFn: (values: LabelForm) =>
      hq.label.print({
        materialId: Number(values.materialId),
        type: values.type,
        code: values.code?.trim() || undefined,
        batchNo: values.batchNo?.trim() || undefined,
        expiryDate: values.expiryDate ? values.expiryDate.format('YYYY-MM-DD') : undefined,
        copies: values.copies
      }),
    onSuccess: (result) => setPreview(result)
  });

  const browserPrint = () => {
    if (!preview) return;
    const win = window.open('', '_blank', 'width=520,height=640');
    if (!win) {
      message.warning('浏览器拦截了弹窗，请允许后重试');
      return;
    }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>标签打印</title>
      <style>@page{margin:6mm}body{margin:0;padding:8px;background:#fff}</style></head>
      <body>${preview.html}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const copyZpl = async () => {
    if (!preview) return;
    await navigator.clipboard.writeText(preview.zpl);
    message.success('ZPL 已复制，可在斑马打印机驱动或打印工具中发送');
  };

  const error = render.error instanceof ApiError
    ? render.error.friendlyMessage
    : render.error ? String(render.error) : null;

  return (
    <>
      <Card className="page-card" title="标签打印">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="条码/RFID 标签与腕带同版式：HTML 可浏览器直打，ZPL 可发斑马等热敏打印机；RFID 需填写 EPC。"
        />
        <Form<LabelForm>
          form={form}
          layout="vertical"
          onFinish={(values) => render.mutate(values)}
          initialValues={{ type: 'BARCODE', copies: 1 }}
          style={{ maxWidth: 720 }}
        >
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="type" label="标签类型" style={{ width: 180 }}>
              <Segmented<LabelType>
                options={[
                  { value: 'BARCODE', label: '条码' },
                  { value: 'RFID', label: 'RFID' }
                ]}
              />
            </Form.Item>
            <Form.Item name="copies" label="份数" style={{ width: 120 }}>
              <InputNumber min={1} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="materialId" label="物资" rules={[{ required: true, message: '请选择物资' }]}>
            <Select
              showSearch
              filterOption={false}
              placeholder="输入物资编码 / 品名搜索"
              onSearch={(value) => setKeyword(value)}
              options={(materials.data?.items ?? []).map((item) => ({
                value: item.id,
                label: `${item.materialName}（${item.materialCode}）`
              }))}
            />
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item
              name="code"
              label={type === 'RFID' ? 'RFID EPC（必填）' : '条码值（留空取物资主码）'}
              style={{ flex: 1 }}
              rules={type === 'RFID' ? [{ required: true, message: 'RFID 标签必须填写 EPC' }] : []}
            >
              <Input placeholder={type === 'RFID' ? '如 E28011902000...' : '留空自动取主数据主码'} />
            </Form.Item>
            <Form.Item name="batchNo" label="批号" style={{ flex: 1 }}>
              <Input placeholder="可选" />
            </Form.Item>
            <Form.Item name="expiryDate" label="效期" style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Button type="primary" htmlType="submit" loading={render.isPending}>生成标签</Button>
        </Form>

        {error ? (
          <Alert type="error" showIcon style={{ marginTop: 16 }} message="标签生成失败" description={error} />
        ) : null}
      </Card>

      {preview ? (
        <Card
          title="打印预览"
          style={{ marginTop: 16 }}
          extra={
            <Space>
              <Button onClick={copyZpl}>复制 ZPL</Button>
              <Button type="primary" onClick={browserPrint}>浏览器打印</Button>
            </Space>
          }
        >
          <Typography.Text type="secondary">
            {preview.type === 'RFID' ? 'RFID EPC' : '条码'}：{preview.code} · {preview.copies} 份
          </Typography.Text>
          <div style={{ marginTop: 12 }} dangerouslySetInnerHTML={{ __html: preview.html }} />
          <Typography.Paragraph type="secondary" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>
            {preview.zpl}
          </Typography.Paragraph>
        </Card>
      ) : null}
    </>
  );
}
